import React, { useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import { enqueueSnackbar } from "notistack";
import {
  addInvoiceCharge,
  createInvoice,
  deleteInvoiceCharge,
  getAllInvoices,
  getCurrentCompanyProfile,
  getInvoice,
  updateInvoiceCharge,
  updateInvoice,
} from "../../services/ValitanApiService";
import tdLetterheadBg from "../../assets/images/TD-letterhead-2026-1.png";

const BRAND_BLUE = "#0147C8";

const getEmptyForm = (selectedBankAccountIds = []) => ({
  issuedDate: "",
  dueDate: "",
  status: "Pending",
  client: "",
  recipientAddress: "",
  invoiceSubject: "",
  notes: "",
  selectedBankAccountIds,
  lineItems: [
    { description: "", amount: "", vat: "18" },
  ],
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Invoices = () => {
  document.title = "Invoices | Valitan IMS";

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", client: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [pendingBankAccountId, setPendingBankAccountId] = useState(null);
  const [letterheadDataUrl, setLetterheadDataUrl] = useState("");
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewRenderKey, setPreviewRenderKey] = useState(0);
  const previewIframeRef = useRef(null);

  const [paginationInfo, setPaginationInfo] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 1,
  });

  const normalizeInvoice = (invoice = {}) => ({
    id: invoice.id,
    invoiceNumber: invoice.invoiceNumber || invoice.invoice_number || "",
    invoiceYear: invoice.invoiceYear || invoice.invoice_year,
    invoiceSeq: invoice.invoiceSeq || invoice.invoice_seq,
    client: invoice.client || "",
    issuedDate: invoice.issuedDate || invoice.issued_date || "",
    dueDate: invoice.dueDate || invoice.due_date || "",
    amount: Number(invoice.totalAmount || invoice.total_amount || 0),
    status: invoice.status || "Pending",
    recipientAddress: invoice.recipientAddress || invoice.recipient_address || "",
    invoiceSubject: invoice.invoiceSubject || invoice.invoice_subject || "",
    notes: invoice.notes || "",
    charges: (invoice.charges || []).map((charge) => ({
      id: charge.id,
      description: charge.description || "",
      amount: Number(charge.amount || 0),
      vatPercent: Number(charge.vatPercent || charge.vat_percent || 0),
      lineTotal: Number(charge.lineTotal || charge.line_total || 0),
      displayOrder: charge.displayOrder || charge.display_order || 0,
    })),
    bankAccountIds: (invoice.bankAccountIds || invoice.bank_account_ids || []).filter(Boolean),
    bankAccounts: (invoice.bankAccounts || invoice.bank_accounts || []).map((account) => ({
      id: account.id,
      accountName: account.accountName || account.account_name || "",
      accountNumber: account.accountNumber || account.account_number || "",
      bankName: account.bankName || account.bank_name || "",
    })),
  });

  const normalizeCompanyProfile = (profile = {}) => {
    const accounts = profile.bankAccounts || profile.bank_accounts || [];
    return {
      companyName: profile.companyName || profile.company_name || "",
      poBox: profile.poBox || profile.po_box || "",
      city: profile.city || "",
      tin: profile.tin || "",
      vrn: profile.vrn || "",
      vat: profile.vat,
      email: profile.email || "",
      phonePrimary: profile.phonePrimary || profile.phone_primary || "",
      phoneSecondary: profile.phoneSecondary || profile.phone_secondary || "",
      physicalAddress: profile.physicalAddress || profile.physical_address || "",
      logo: profile.logo || "",
      bankAccounts: accounts.map((account) => ({
        id: account.id,
        accountName: account.accountName || account.account_name || "",
        accountNumber: account.accountNumber || account.account_number || "",
        bankName: account.bankName || account.bank_name || "",
      })),
    };
  };

  const fetchCompanyProfile = async () => {
    try {
      const response = await getCurrentCompanyProfile();
      setCompanyProfile(normalizeCompanyProfile(response?.data?.data || {}));
    } catch (error) {
      console.error("Error loading company profile for invoice preview:", error);
    }
  };

  const fetchInvoices = async (pageNumber = paginationInfo.pageNumber, pageSize = paginationInfo.pageSize) => {
    setIsLoading(true);
    try {
      const response = await getAllInvoices(pageNumber, pageSize);
      const page = response?.data?.data || {};
      const content = page.content || [];
      setInvoices(content.map(normalizeInvoice));
      setPaginationInfo({
        pageNumber: page.number ?? pageNumber,
        pageSize: page.size ?? pageSize,
        totalElements: page.totalElements ?? 0,
        totalPages: Math.max(1, page.totalPages ?? 1),
      });
    } catch (error) {
      console.error("Error loading invoices:", error);
      enqueueSnackbar("Failed to load invoices.", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(0, paginationInfo.pageSize);
    fetchCompanyProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetch(tdLetterheadBg)
      .then((res) => res.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (isMounted) {
            setLetterheadDataUrl(typeof reader.result === "string" ? reader.result : "");
          }
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {
        if (isMounted) {
          setLetterheadDataUrl("");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredInvoices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return invoices.filter((invoice) => {
      const effectiveStatus = getEffectiveStatus(invoice);
      const statusMatch = filters.status ? effectiveStatus === filters.status : true;
      const clientMatch = filters.client
        ? invoice.client.toLowerCase().includes(filters.client.toLowerCase())
        : true;
      const searchMatch = query
        ? `${invoice.invoiceNumber} ${invoice.client} ${effectiveStatus}`.toLowerCase().includes(query)
        : true;
      return statusMatch && clientMatch && searchMatch;
    });
  }, [filters.client, filters.status, invoices, searchQuery]);

  const computedTotal = useMemo(() => {
    return formData.lineItems.reduce((sum, item) => {
      const amount = toNumber(item.amount);
      const vat = toNumber(item.vat);
      return sum + amount + (amount * vat) / 100;
    }, 0);
  }, [formData.lineItems]);

  const refreshPagination = (pageNumber = 0, pageSize = paginationInfo.pageSize) => {
    const safePageSize = Math.max(1, Number(pageSize) || paginationInfo.pageSize);
    const safePageNumber = Math.max(0, Number(pageNumber) || 0);
    fetchInvoices(safePageNumber, safePageSize);
  };

  const handleApplyFilters = () => {
    refreshPagination(0, paginationInfo.pageSize);
  };

  const handleResetFilters = () => {
    setFilters({ status: "", client: "" });
    setSearchQuery("");
    fetchInvoices(0, paginationInfo.pageSize);
  };

  const handlePageChange = (pageNumber, pageSize) => refreshPagination(pageNumber, pageSize);
  const handlePageSizeChange = (newPageSize) => refreshPagination(0, newPageSize);
  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber <= 0) return;
    refreshPagination(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
  };
  const handleNextPage = () => {
    if (paginationInfo.pageNumber >= paginationInfo.totalPages - 1) return;
    refreshPagination(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
  };

  const openCreateModal = () => {
    setEditingInvoice(null);
    setPendingBankAccountId(null);
    setFormData(getEmptyForm([]));
    setIsModalOpen(true);
  };

  const toggleViewModal = () => setIsViewModalOpen((prev) => !prev);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setPendingBankAccountId(null);
    setFormData(getEmptyForm());
    setEditingInvoice(null);
  };

  const bankAccountOptions = useMemo(
    () =>
      (companyProfile?.bankAccounts || []).map((account) => ({
        value: account.id,
        label: `${account.bankName || "Bank"} — ${account.accountName || "-"} (${account.accountNumber || "-"})`,
        accountName: account.accountName || "-",
        accountNumber: account.accountNumber || "-",
        bankName: account.bankName || "Bank",
      })),
    [companyProfile]
  );

  const availableBankAccountOptions = useMemo(
    () =>
      bankAccountOptions.filter(
        (option) => !(formData.selectedBankAccountIds || []).includes(option.value)
      ),
    [bankAccountOptions, formData.selectedBankAccountIds]
  );

  const selectedBankAccounts = useMemo(
    () =>
      (formData.selectedBankAccountIds || [])
        .map((id) => bankAccountOptions.find((option) => option.value === id))
        .filter(Boolean),
    [bankAccountOptions, formData.selectedBankAccountIds]
  );

  const addSelectedBankAccount = () => {
    if (!pendingBankAccountId) {
      enqueueSnackbar("Please select a bank account first.", { variant: "warning" });
      return;
    }
    if ((formData.selectedBankAccountIds || []).includes(pendingBankAccountId)) {
      enqueueSnackbar("That bank account is already added.", { variant: "info" });
      setPendingBankAccountId(null);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      selectedBankAccountIds: [...(prev.selectedBankAccountIds || []), pendingBankAccountId],
    }));
    setPendingBankAccountId(null);
  };

  const removeSelectedBankAccount = (accountId) => {
    setFormData((prev) => ({
      ...prev,
      selectedBankAccountIds: (prev.selectedBankAccountIds || []).filter((id) => id !== accountId),
    }));
  };

  function getEffectiveStatus(invoice) {
    const currentStatus = invoice?.status || "Pending";
    if (currentStatus === "Paid" || currentStatus === "Cancelled") return currentStatus;

    const dueDateValue = invoice?.dueDate;
    if (!dueDateValue) return currentStatus;

    const dueDate = new Date(`${dueDateValue}T00:00:00`);
    if (Number.isNaN(dueDate.getTime())) return currentStatus;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today ? "Overdue" : currentStatus;
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    const formatted = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
    return formatted.replace(/ (\d{4})$/, ", $1");
  };

  const buildInvoicePreviewHtml = (invoice, company) => {
    if (!invoice) return "";

    // Use dataURL so iframe srcDoc always has access to the image
    const letterheadBg = letterheadDataUrl || "";

    const notesText = typeof invoice.notes === "string" ? invoice.notes.trim() : "";

    const charges = [...(invoice.charges || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    const chargeRows = charges.length
      ? charges
        .map((charge, index) => {
          const amount = Number(charge.amount || 0);
          const vat = Number(charge.vatPercent || 0);
          const lineTotal = Number(charge.lineTotal || amount + (amount * vat) / 100);
          return `
            <tr>
              <td style="text-align:center;">${index + 1}</td>
              <td>${charge.description || "-"}</td>
              <td style="text-align:right;">${Math.round(amount).toLocaleString()}</td>
              <td style="text-align:right;">${vat}%</td>
              <td style="text-align:right;">${Math.round(lineTotal).toLocaleString()}</td>
            </tr>
          `;
        })
        .join("")
      : `<tr><td colspan="5" style="text-align:center">No charges available</td></tr>`;

    const companyAccounts = company?.bankAccounts || [];
    const selectedIds = invoice.bankAccountIds || [];
    let bankAccounts = [];
    if ((invoice.bankAccounts || []).length > 0) {
      bankAccounts = invoice.bankAccounts;
    } else if (selectedIds.length > 0) {
      bankAccounts = companyAccounts.filter((account) => selectedIds.includes(account.id));
    } else {
      // Legacy invoices without a saved selection: keep previous "show all" behaviour.
      bankAccounts = companyAccounts;
    }
    const bankHtml = bankAccounts.length
      ? bankAccounts
        .map(
          (account, index) => `
          <div class="bank-block">
            <div><strong>NAMBA YA AKAUNTI:</strong> ${account.accountNumber || "-"}</div>
            <div><strong>JINA LA AKAUNTI:</strong> ${account.accountName || "-"}</div>
            <div><strong>JINA LA BANK:</strong> ${account.bankName || "-"}</div>
          </div>
          ${index < bankAccounts.length - 1 ? "<div style='margin:10px 0;'>Au</div>" : ""}
        `
        )
        .join("")
      : `<div class="bank-block">No bank account details found.</div>`;

    return `
    <!doctype html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>${invoice.invoiceNumber || "Invoice"}</title>
     <style>
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  @page { size: A4; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }

  /* ✅ Center the A4 page inside the iframe viewport */
  .sheet {
    width: 100%;
    min-height: 100vh;
    display: flex;
    justify-content: center;   /* horizontal centering */
    align-items: flex-start;   /* keep top aligned */
    background: #fff;
  }

  .page {
    width: 210mm;
    height: 297mm;
    position: relative;
    overflow: hidden;
    font-family: Arial, sans-serif;
    color: #111;
    background: #fff;
  }

  .bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: fill;
    z-index: 0;
    pointer-events: none;
    user-select: none;
  }

  /* ✅ Reduce top space + keep content stable */
  .content {
    position: relative;
    z-index: 1;
    padding: 34mm 22mm 30mm;
    width: 100%;
    box-sizing: border-box;
  }

  /* ✅ Typography (slightly smaller) */
  .title-right {
    text-align: right;
    font-size: 12px;
    line-height: 1.28;
    font-weight: 700;
  }

  .section {
    margin-top: 12px;
    font-size: 12px;
    line-height: 1.35;
    font-weight: 700;
  }

  .subject {
    margin: 12px 0 8px;
    font-size: 13px;
    font-weight: 800;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
    font-size: 11.5px;
  }

  th, td {
    border: 1px solid #111;
    padding: 5px 7px;
    vertical-align: top;
  }

  th {
    text-align: center;
    font-weight: 800;
  }

  .right { text-align: right; }
  .sum-row td { font-weight: 800; }

  .bank-block {
    margin-bottom: 8px;
    font-size: 11.5px;
    line-height: 1.35;
  }

  .notes {
    margin-top: 12px;
    font-size: 11.5px;
    line-height: 1.4;
  }
</style>

    </head>
    <body>
    <div class="sheet">
      <div class="page">
        ${letterheadBg ? `<img class="bg" src="${letterheadBg}" alt="Letterhead" />` : ""}
        <div class="content">
          <div class="title-right">
            ANKARA NO: ${invoice.invoiceNumber || "-"}<br/>
            ${company?.companyName || "-"}<br/>
            ${company?.poBox || "-"}, ${company?.city || "-"}<br/>
            Tarehe: ${formatDate(invoice.issuedDate)}<br/><br/>
            TIN: ${company?.tin || "-"}<br/>
            VRN: ${company?.vrn || "-"}
          </div>

          <div class="section">
            <strong>ANKARA KWA:</strong><br/>
            ${(invoice.client || "-").toUpperCase()}<br/>
            ${(invoice.recipientAddress || "-").toUpperCase()}
          </div>

          <div class="subject">${(invoice.invoiceSubject || "-").toUpperCase()}</div>

          <table>
            <thead>
              <tr>
                <th style="width:7%;">S/N</th>
                <th>MAELEZO</th>
                <th style="width:18%;">GHARAMA</th>
                <th style="width:18%;">VAT (%)</th>
                <th style="width:20%;">JUMLA</th>
              </tr>
            </thead>
            <tbody>${chargeRows}</tbody>
            <tfoot>
              <tr class="sum-row">
                <td colspan="4" class="right">JUMLA KUU</td>
                <td class="right">${Math.round(invoice.amount || 0).toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>

          <div class="notes">
            <strong>Njia za malipo;</strong><br/>
            Tafadhali lipia kupitia njia zifuatazo za malipo:
            <div style="margin-top:10px;">${bankHtml}</div>

            ${notesText
        ? `
              <br/>
              <strong>Maelezo:</strong>
              <ol style="margin:6px 0 0 20px; padding:0;">
                <li>${notesText}</li>
              </ol>
            `
        : ""
      }
          </div>
        </div>
      </div>
      </div>
    </body>
    </html>
  `;
  };


  const getExportFileBase = () => {
    const raw = selectedInvoice?.invoiceNumber || "invoice";
    return raw.replace(/[^\w.-]+/g, "_");
  };

  const handleExportPdf = () => {
    const frame = previewIframeRef.current;
    if (!frame?.contentWindow) {
      enqueueSnackbar("Preview is not ready for PDF export.", { variant: "warning" });
      return;
    }
    frame.contentWindow.focus();
    frame.contentWindow.print();
  };

  const handleDownloadPdf = async () => {
    try {
      const frame = previewIframeRef.current;
      const doc = frame?.contentDocument || frame?.contentWindow?.document;
      if (!doc) {
        enqueueSnackbar("Preview is not ready for download.", { variant: "warning" });
        return;
      }

      const pageEl = doc.querySelector(".page");
      if (!pageEl) {
        enqueueSnackbar("No content found to export.", { variant: "warning" });
        return;
      }

      // Ensure images are loaded (especially the background img)
      const imgs = Array.from(doc.images || []);
      await Promise.all(
        imgs.map(
          (img) =>
            img.complete
              ? Promise.resolve()
              : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve; // don't block forever
              })
        )
      );

      const canvas = await html2canvas(pageEl, {
        scale: 2, // sharper PDF
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      // Fit image exactly to A4
      const pdfW = 210;
      const pdfH = 297;
      pdf.addImage(imgData, "JPEG", 0, 0, pdfW, pdfH);

      pdf.save(`${getExportFileBase()}.pdf`);
    } catch (error) {
      console.error("PDF export failed:", error);
      enqueueSnackbar("Failed to export PDF.", { variant: "error" });
    }
  };


  const handleZoomIn = () => setPreviewZoom((prev) => Math.min(2, Number((prev + 0.1).toFixed(2))));
  const handleZoomOut = () => setPreviewZoom((prev) => Math.max(0.6, Number((prev - 0.1).toFixed(2))));
  const handleZoomReset = () => setPreviewZoom(1);
  const handlePreviewRefresh = () => setPreviewRenderKey((prev) => prev + 1);

  const handleViewInvoice = (invoiceId) => {
    if (!invoiceId) return;
    setIsViewLoading(true);
    getInvoice(invoiceId)
      .then((response) => {
        const data = response?.data?.data || {};
        const normalized = normalizeInvoice(data);
        setSelectedInvoice(normalized);
        setIsViewModalOpen(true);
        if (!companyProfile) {
          fetchCompanyProfile();
        }
      })
      .catch((error) => {
        console.error("Error loading invoice details:", error);
        const message = error?.response?.data?.message || "Failed to load invoice details.";
        enqueueSnackbar(message, { variant: "error" });
      })
      .finally(() => setIsViewLoading(false));
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.lineItems];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, lineItems: updated };
    });
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: "", amount: "", vat: "0" }],
    }));
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const submitInvoice = async (event) => {
    event.preventDefault();

    if (!formData.client || !formData.issuedDate || !formData.dueDate) {
      enqueueSnackbar("Please fill Client, Issue Date and Due Date.", { variant: "warning" });
      return;
    }

    if (computedTotal <= 0) {
      enqueueSnackbar("Please add at least one charge line with amount.", { variant: "warning" });
      return;
    }

    const availableBankAccounts = companyProfile?.bankAccounts || [];
    if (availableBankAccounts.length > 0 && (formData.selectedBankAccountIds || []).length === 0) {
      enqueueSnackbar("Please select at least one bank account for this invoice.", { variant: "warning" });
      return;
    }

    const validLineItems = formData.lineItems
      .filter((item) => item.description || toNumber(item.amount) > 0)
      .map((item, index) => ({
        id: item.id || null,
        description: item.description || `Charge ${index + 1}`,
        amount: toNumber(item.amount),
        vatPercent: toNumber(item.vat),
        displayOrder: index,
      }));

    try {
      if (editingInvoice?.id) {
        const updatePayload = {
          invoiceNumber: editingInvoice.invoiceNumber,
          invoiceYear: editingInvoice.invoiceYear,
          invoiceSeq: editingInvoice.invoiceSeq,
          issuedDate: formData.issuedDate,
          dueDate: formData.dueDate,
          status: formData.status,
          client: formData.client,
          recipientAddress: formData.recipientAddress,
          invoiceSubject: formData.invoiceSubject,
          notes: formData.notes,
          bankAccountIds: formData.selectedBankAccountIds || [],
        };

        await updateInvoice(editingInvoice.id, updatePayload);

        const existingChargeIds = (editingInvoice.charges || []).map((charge) => charge.id).filter(Boolean);
        const currentChargeIds = validLineItems.map((item) => item.id).filter(Boolean);
        const removedChargeIds = existingChargeIds.filter((id) => !currentChargeIds.includes(id));

        for (let i = 0; i < validLineItems.length; i += 1) {
          const item = validLineItems[i];
          const chargePayload = {
            description: item.description,
            amount: item.amount,
            vatPercent: item.vatPercent,
            displayOrder: i,
          };

          if (item.id) {
            await updateInvoiceCharge(editingInvoice.id, item.id, chargePayload);
          } else {
            await addInvoiceCharge(editingInvoice.id, chargePayload);
          }
        }

        if (removedChargeIds.length > 0) {
          await Promise.all(removedChargeIds.map((id) => deleteInvoiceCharge(editingInvoice.id, id)));
        }

        enqueueSnackbar("Invoice updated successfully.", { variant: "success" });
      } else {
        const issuedYear = new Date(formData.issuedDate).getFullYear();
        const sameYear = invoices.filter((invoice) => Number(invoice.invoiceYear) === issuedYear);
        const maxSeq = sameYear.reduce((max, invoice) => Math.max(max, Number(invoice.invoiceSeq || 0)), 0);
        const nextSeq = maxSeq + 1;
        const generatedNumber = `TD/VAL/${issuedYear}/${String(nextSeq).padStart(6, "0")}`;

        const createPayload = {
          invoiceNumber: generatedNumber,
          invoiceYear: issuedYear,
          invoiceSeq: nextSeq,
          issuedDate: formData.issuedDate,
          dueDate: formData.dueDate,
          status: formData.status,
          client: formData.client,
          recipientAddress: formData.recipientAddress,
          invoiceSubject: formData.invoiceSubject,
          notes: formData.notes,
          bankAccountIds: formData.selectedBankAccountIds || [],
          charges: validLineItems.map((item, index) => ({
            description: item.description,
            amount: item.amount,
            vatPercent: item.vatPercent,
            displayOrder: index,
          })),
        };

        await createInvoice(createPayload);
        enqueueSnackbar("Invoice added successfully.", { variant: "success" });
      }

      setIsModalOpen(false);
      setFormData(getEmptyForm());
      setEditingInvoice(null);
      fetchInvoices(0, paginationInfo.pageSize);
    } catch (error) {
      console.error("Error saving invoice:", error);
      const message = error?.response?.data?.message || "Failed to save invoice.";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleEditInvoice = (invoiceId) => {
    if (!invoiceId) return;
    getInvoice(invoiceId)
      .then((response) => {
        const normalized = normalizeInvoice(response?.data?.data || {});
        setEditingInvoice(normalized);
        setPendingBankAccountId(null);
        setFormData({
          issuedDate: normalized.issuedDate || "",
          dueDate: normalized.dueDate || "",
          status: normalized.status || "Pending",
          client: normalized.client || "",
          recipientAddress: normalized.recipientAddress || "",
          invoiceSubject: normalized.invoiceSubject || "",
          notes: normalized.notes || "",
          selectedBankAccountIds: [...(normalized.bankAccountIds || [])],
          lineItems: (normalized.charges || []).length > 0
            ? [...normalized.charges]
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((charge) => ({
                id: charge.id,
                description: charge.description || "",
                amount: String(charge.amount ?? ""),
                vat: String(charge.vatPercent ?? "0"),
              }))
            : [{ description: "", amount: "", vat: "18" }],
        });
        setIsModalOpen(true);
      })
      .catch((error) => {
        console.error("Error loading invoice for edit:", error);
        const message = error?.response?.data?.message || "Failed to load invoice for edit.";
        enqueueSnackbar(message, { variant: "error" });
      });
  };

  const updateInvoiceStatus = (invoiceId, target, nextStatus) => {
    const payload = {
      invoiceNumber: target.invoiceNumber,
      invoiceYear: target.invoiceYear,
      invoiceSeq: target.invoiceSeq,
      issuedDate: target.issuedDate,
      dueDate: target.dueDate,
      status: nextStatus,
      client: target.client,
      recipientAddress: target.recipientAddress,
      invoiceSubject: target.invoiceSubject,
      notes: target.notes,
    };

    updateInvoice(invoiceId, payload)
      .then(() => {
        fetchInvoices(paginationInfo.pageNumber, paginationInfo.pageSize);
        enqueueSnackbar(`Invoice ${target.invoiceNumber} updated to ${nextStatus}.`, { variant: "success" });
      })
      .catch((error) => {
        console.error("Error updating invoice:", error);
        const message = error?.response?.data?.message || "Failed to update invoice status.";
        enqueueSnackbar(message, { variant: "error" });
      });
  };

  const runAction = (action, invoiceId) => {
    if (action === "View") {
      handleViewInvoice(invoiceId);
      return;
    }

    if (action === "Edit") {
      handleEditInvoice(invoiceId);
      return;
    }

    if (action !== "Mark as paid" && action !== "Cancel") {
      enqueueSnackbar(`${action} action clicked for ${invoiceId}`, { variant: "info" });
      return;
    }

    const target = invoices.find((invoice) => invoice.id === invoiceId);
    if (!target) return;
    const effectiveStatus = getEffectiveStatus(target);

    if (action === "Mark as paid" && effectiveStatus === "Cancelled") {
      enqueueSnackbar("This invoice is cancelled and cannot be marked as paid.", { variant: "warning" });
      return;
    }

    if (action === "Mark as paid") {
      if (effectiveStatus === "Paid") {
        enqueueSnackbar("This invoice is already marked as paid.", { variant: "info" });
        return;
      }

      confirmDialog({
        message: `Are you sure you want to mark invoice ${target.invoiceNumber} as paid?`,
        header: "Mark Invoice as Paid",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes, Mark Paid",
        rejectLabel: "No",
        accept: () => updateInvoiceStatus(invoiceId, target, "Paid"),
        reject: () => {},
      });
      return;
    }

    if (action === "Cancel") {
      if (effectiveStatus === "Paid") {
        enqueueSnackbar("This invoice is already paid and cannot be cancelled.", { variant: "warning" });
        return;
      }
      confirmDialog({
        message: `Are you sure you want to cancel invoice ${target.invoiceNumber}?`,
        header: "Cancel Invoice",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes, Cancel",
        rejectLabel: "No",
        accept: () => updateInvoiceStatus(invoiceId, target, "Cancelled"),
        reject: () => {},
      });
      return;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Payments" breadcrumbItem="Invoices" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <Button
                      style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                      className="add-btn float-end"
                      onClick={openCreateModal}
                    >
                      <i className="ri-add-line align-bottom me-1" /> Add Invoice
                    </Button>
                  </h4>
                </CardHeader>
                <CardBody>
                  <Form className="mb-3">
                    <Row className="g-3">
                      <Col md={3}>
                        <FormGroup>
                          <Label>Status</Label>
                          <Input
                            type="select"
                            value={filters.status}
                            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                          >
                            <option value="">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Paid">Paid</option>
                            <option value="Overdue">Overdue</option>
                            <option value="Cancelled">Cancelled</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Client</Label>
                          <Input
                            type="text"
                            placeholder="Filter by client..."
                            value={filters.client}
                            onChange={(e) => setFilters((prev) => ({ ...prev, client: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={5} className="d-flex justify-content-end align-items-end">
                        <Button
                          className="me-2"
                          style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </Button>
                        <Button color="secondary" onClick={handleResetFilters}>
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </Form>

                  <Row className="g-4 mb-3">
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <div className="search-box ms-2">
                          <input
                            type="text"
                            className="form-control search"
                            placeholder="Search invoice no/client/status..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive table-card mt-3 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Invoice No</th>
                          <th>Client</th>
                          <th>Issued Date</th>
                          <th>Due Date</th>
                          <th>Amount (TZS)</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.length > 0 ? (
                          filteredInvoices.map((invoice, idx) => {
                            const effectiveStatus = getEffectiveStatus(invoice);
                            return (
                              <tr key={invoice.id || invoice.invoiceNumber}>
                              <td>{paginationInfo.pageNumber * paginationInfo.pageSize + idx + 1}</td>
                              <td>{invoice.invoiceNumber}</td>
                              <td>{invoice.client}</td>
                              <td>{invoice.issuedDate}</td>
                              <td>{invoice.dueDate}</td>
                              <td>{Math.round(invoice.amount).toLocaleString()}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    effectiveStatus === "Paid"
                                      ? "bg-success"
                                      : effectiveStatus === "Pending"
                                        ? "bg-warning text-dark"
                                        : effectiveStatus === "Cancelled"
                                          ? "bg-secondary"
                                        : "bg-danger"
                                  }`}
                                >
                                  {effectiveStatus}
                                </span>
                              </td>
                              <td>
                                <div className="btn-group" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                    onClick={() => runAction("View", invoice.id)}
                                  >
                                    <i className="ri-eye-line" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                    onClick={() => runAction("Edit", invoice.id)}
                                  >
                                    <i className="ri-edit-box-line" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-success btn-sm"
                                    disabled={effectiveStatus === "Cancelled"}
                                    title={effectiveStatus === "Cancelled" ? "Cancelled invoice cannot be paid" : "Mark as paid"}
                                    onClick={() => runAction("Mark as paid", invoice.id)}
                                  >
                                    <i className="ri-check-line" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => runAction("Cancel", invoice.id)}
                                  >
                                    <i className="ri-close-line" />
                                  </button>
                                </div>
                              </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No invoice records found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    paginationInfo={paginationInfo}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                  {isLoading ? <p className="mt-2 mb-0 text-muted">Loading invoices...</p> : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={isModalOpen} toggle={handleModalClose} size="xl" backdrop="static">
        <ModalHeader toggle={handleModalClose}>{editingInvoice ? "Edit Invoice" : "Add Invoice"}</ModalHeader>
        <Form onSubmit={submitInvoice}>
          <ModalBody>
            <Row className="g-3">
              <Col md={4}>
                <FormGroup>
                  <Label>Issue Date</Label>
                  <Input
                    type="date"
                    value={formData.issuedDate}
                    onChange={(e) => handleFormChange("issuedDate", e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleFormChange("dueDate", e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={formData.status}
                    onChange={(e) => handleFormChange("status", e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Overdue">Overdue</option>
                    <option value="Cancelled">Cancelled</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Client (Ankara Kwa)</Label>
                  <Input
                    type="text"
                    value={formData.client}
                    onChange={(e) => handleFormChange("client", e.target.value)}
                    placeholder="e.g Noran Mohamed Kombo"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Recipient Address</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    value={formData.recipientAddress}
                    onChange={(e) => handleFormChange("recipientAddress", e.target.value)}
                    placeholder="e.g S.L.P: 1249, Dodoma"
                  />
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label>Invoice Subject / Purpose</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    value={formData.invoiceSubject}
                    onChange={(e) => handleFormChange("invoiceSubject", e.target.value)}
                    placeholder="e.g Ankara kwa ajili ya uthamini..."
                  />
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup className="mb-0">
                  <Label>Bank Account(s) on Invoice</Label>
                  {bankAccountOptions.length === 0 ? (
                    <div className="text-muted small">
                      No company bank accounts found. Add them under Company Profile first.
                    </div>
                  ) : (
                    <>
                      <div className="d-flex gap-2 align-items-start">
                        <div className="flex-grow-1">
                          <Select
                            isClearable
                            isSearchable
                            options={availableBankAccountOptions}
                            value={
                              availableBankAccountOptions.find(
                                (option) => option.value === pendingBankAccountId
                              ) || null
                            }
                            onChange={(selected) =>
                              setPendingBankAccountId(selected ? selected.value : null)
                            }
                            placeholder="Select bank account to use..."
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              container: (base) => ({ ...base, flex: 1 }),
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          className="align-self-stretch px-3"
                          onClick={addSelectedBankAccount}
                          disabled={!pendingBankAccountId}
                          style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE, color: "#fff" }}
                        >
                          <i className="ri-add-line" /> Add
                        </Button>
                      </div>

                      {selectedBankAccounts.length > 0 ? (
                        <div className="table-responsive mt-2">
                          <table className="table table-bordered align-middle mb-0">
                            <thead>
                              <tr>
                                <th style={{ width: "70px" }}>S/N</th>
                                <th>Bank</th>
                                <th>Account Name</th>
                                <th>Account Number</th>
                                <th style={{ width: "70px" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedBankAccounts.map((account, index) => (
                                <tr key={account.value}>
                                  <td>{index + 1}</td>
                                  <td>{account.bankName}</td>
                                  <td>{account.accountName}</td>
                                  <td>{account.accountNumber}</td>
                                  <td className="text-center">
                                    <Button
                                      type="button"
                                      color="danger"
                                      size="sm"
                                      outline
                                      onClick={() => removeSelectedBankAccount(account.value)}
                                      title="Remove"
                                    >
                                      <i className="ri-delete-bin-line" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-muted small mt-2">
                          No bank accounts added yet. Select an account and click Add.
                        </div>
                      )}
                    </>
                  )}
                </FormGroup>
              </Col>

            </Row>

            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="mb-0">Invoice Charges</Label>
                <Button
                  size="sm"
                  type="button"
                  onClick={addLineItem}
                  style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE, color: "#fff" }}
                >
                  <i className="ri-add-line" /> Add Row
                </Button>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "70px" }}>S/N</th>
                      <th style={{ width: "38%" }}>Description</th>
                      <th>Amount</th>
                      <th>VAT %</th>
                      <th style={{ width: "150px" }}>Total</th>
                      <th style={{ width: "70px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lineItems.map((item, index) => {
                      const amount = toNumber(item.amount);
                      const vat = toNumber(item.vat);
                      const lineTotal = amount + (amount * vat) / 100;

                      return (
                        <tr key={`item-${index}`}>
                          <td>{index + 1}</td>
                          <td>
                            <Input
                              type="textarea"
                              rows="2"
                              value={item.description}
                              onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                              placeholder="e.g Ada ya huduma"
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={item.amount}
                              onChange={(e) => handleLineItemChange(index, "amount", e.target.value)}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              value={item.vat}
                              onChange={(e) => handleLineItemChange(index, "vat", e.target.value)}
                              placeholder="0"
                            />
                          </td>
                          <td>{Math.round(lineTotal).toLocaleString()}</td>
                          <td>
                            <Button
                              color="danger"
                              size="sm"
                              type="button"
                              onClick={() => removeLineItem(index)}
                              disabled={formData.lineItems.length <= 1}
                            >
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <Row className="mt-3">
              <Col md={8}>
                <FormGroup>
                  <Label>Notes</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    value={formData.notes}
                    onChange={(e) => handleFormChange("notes", e.target.value)}
                    placeholder="e.g Malipo yanapaswa kufanywa kufikia tarehe iliyowekwa."
                  />
                </FormGroup>
              </Col>
              <Col md={4} className="d-flex align-items-end justify-content-end">
                <h5 className="mb-0">Total: TZS {Math.round(computedTotal).toLocaleString()}</h5>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button type="button" color="light" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button type="submit" style={{ backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}>
              {editingInvoice ? "Update Invoice" : "Save Invoice"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={isViewModalOpen} toggle={toggleViewModal} size="xl">
        <ModalHeader toggle={toggleViewModal}>
          Invoice Preview{selectedInvoice?.invoiceNumber ? ` - ${selectedInvoice.invoiceNumber}` : ""}
        </ModalHeader>
        <ModalBody>
          {isViewLoading ? (
            <p className="mb-0">Loading invoice details...</p>
          ) : selectedInvoice ? (
            <React.Fragment>
              <div className="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-2 bg-light">
                <div className="d-flex align-items-center gap-1">
                  <Button color="light" size="sm" disabled>
                    <i className="ri-skip-left-line" />
                  </Button>
                  <Button color="light" size="sm" disabled>
                    <i className="ri-arrow-left-s-line" />
                  </Button>
                  <Input
                    type="text"
                    value="1"
                    readOnly
                    style={{ width: "52px", height: "30px", textAlign: "center" }}
                  />
                  <span className="text-muted">of 1</span>
                  <Button color="light" size="sm" disabled>
                    <i className="ri-arrow-right-s-line" />
                  </Button>
                  <Button color="light" size="sm" disabled>
                    <i className="ri-skip-right-line" />
                  </Button>
                </div>

                <div className="d-flex align-items-center gap-1">
                  <Button color="light" size="sm" onClick={handleZoomOut} title="Zoom out">
                    <i className="ri-zoom-out-line" />
                  </Button>
                  <Button color="light" size="sm" onClick={handleZoomIn} title="Zoom in">
                    <i className="ri-zoom-in-line" />
                  </Button>
                  <Button color="light" size="sm" onClick={handleZoomReset} title="Reset zoom">
                    {(previewZoom * 100).toFixed(0)}%
                  </Button>
                  <Button color="light" size="sm" onClick={handlePreviewRefresh} title="Refresh">
                    <i className="ri-refresh-line" />
                  </Button>
                  <Button color="light" size="sm" onClick={handleExportPdf} title="Print">
                    <i className="ri-printer-line" />
                  </Button>
                    <Button color="light" size="sm" onClick={handleDownloadPdf} disabled={!letterheadDataUrl}>
                      <i className="ri-download-2-line" />
                    </Button>
                </div>
              </div>

              <div style={{ maxHeight: "70vh", overflow: "auto", border: "1px solid #e9ecef", borderRadius: "6px" }}>
                <div style={{ width: "100%", minHeight: `${620 * previewZoom}px` }}>
                  <iframe
                    key={previewRenderKey}
                    ref={previewIframeRef}
                    title="invoice-preview"
                    style={{
                      width: `${100 / previewZoom}%`,
                      minHeight: "620px",
                      border: "none",
                      transform: `scale(${previewZoom})`,
                      transformOrigin: "top left",
                    }}
                    srcDoc={buildInvoicePreviewHtml(selectedInvoice, companyProfile)}
                  />
                </div>
              </div>
            </React.Fragment>
          ) : (
            <p className="mb-0">No invoice selected.</p>
          )}
        </ModalBody>
      </Modal>
      <ConfirmDialog />
    </React.Fragment>
  );
};

export default Invoices;
